import { IntentProps, useFullTheme } from '~/design';
import { Label } from '../Label';
import { HelpText } from '../HelpText';
import { ReactNode } from 'react';
import { useId } from '~/utils';

export interface FormControlContainerProps extends IntentProps {
  children: ReactNode;
  disabled?: boolean;
  helpText?: string;
  helpTextId?: string;
  id?: string;
  invalid?: boolean;
  label?: ReactNode;
  labelId?: string;
  labelPosition?: 'top' | 'start';
  required?: boolean;
}

export function FormControlContainer({
  children,
  disabled,
  helpText,
  helpTextId,
  id,
  intent,
  invalid,
  label,
  labelId,
  labelPosition = 'top',
  required,
  ...labelProps
}: FormControlContainerProps) {
  const { spacing } = useFullTheme();
  const { adjustedIntent } = useFormControlProps({ disabled, intent, invalid });
  const isInline = labelPosition === 'start';
  const numRows = !isInline ? 0 : helpText ? 2 : 1;

  return (
    <div
      css={[
        {
          width: '100%',
        },
        isInline && {
          columnGap: spacing[2],
          display: 'grid',
          gridTemplateAreas:
            helpText && label
              ? '"label control" "empty helptext"'
              : helpText
                ? '"control" "helptext"'
                : label
                  ? '"label control"'
                  : undefined,
          gridTemplateColumns: label ? 'auto 1fr' : '1fr',
          gridTemplateRows: `repeat(${numRows}, auto)`,
          rowGap: spacing[2],
        },
        !isInline && {
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[2],
        },
      ]}
    >
      {label && (
        <div css={{ gridArea: 'label', alignSelf: isInline ? 'center' : undefined }}>
          <Label
            disabled={disabled}
            htmlFor={id}
            id={labelId}
            label={label}
            required={required}
            {...labelProps}
          />
        </div>
      )}

      <div css={{ gridArea: 'control' }}>{children}</div>

      {helpText && (
        <div css={{ gridArea: 'helptext' }}>
          <HelpText
            id={helpTextId}
            intent={adjustedIntent}
            showIcon={invalid || disabled || intent !== 'secondary'}
          >
            {helpText}
          </HelpText>
        </div>
      )}
    </div>
  );
}

export function useFormControlProps({
  disabled,
  intent,
  invalid,
}: Pick<FormControlContainerProps, 'disabled' | 'invalid' | 'intent'>) {
  const id = useId();
  const adjustedIntent = disabled ? 'secondary' : invalid ? 'negative' : intent;

  return {
    adjustedIntent,
    helpTextId: `${id}-helptext`,
    id,
    labelId: `${id}-label`,
  };
}
